import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_TYPES = [
  { value: '호기심형_진행자', label: '🎤 호기심형 진행자', desc: '청취자 대신 질문, 모르는 척 설명 유도' },
  { value: '정보형_진행자', label: '📋 정보형 진행자', desc: '핵심 정리, 흐름 조율, 요약' },
  { value: '전문가_게스트', label: '🔬 전문가 게스트', desc: '깊이 있는 전문 지식, 데이터/연구 기반' },
  { value: '경험형_게스트', label: '💼 경험형 게스트', desc: '현장 경험, 실제 사례 공유' },
  { value: '반박형_코호스트', label: '⚡ 반박형 코호스트', desc: '다른 관점, 건강한 논쟁 유발' },
  { value: '공감형_코호스트', label: '💙 공감형 코호스트', desc: '청취자 감정 대변, 현실적 반응' },
  { value: '엔터형_코호스트', label: '😄 엔터형 코호스트', desc: '유머, 분위기 전환, 긴장감 해소' },
  { value: '스토리형_게스트', label: '📖 스토리형 게스트', desc: '감성 스토리텔링, 경험담 위주' },
];

const PERSONALITIES = [
  '논리적이고 차분한', '열정적이고 에너지 넘치는', '유머러스하고 가벼운',
  '진지하고 깊이 있는', '친근하고 편안한', '날카롭고 도전적인',
  '따뜻하고 공감 잘 하는', '직설적이고 솔직한',
];

const defaultHost = () => ({
  name: '', job: '', roleType: '호기심형_진행자',
  personality: '', interests: '', episodeRole: '',
});

export default function PodcastSetupPage() {
  const navigate = useNavigate();
  const { api } = useAuth();

  const [step, setStep] = useState(1); // 1: 출연진, 2: 에피소드
  const [hostCount, setHostCount] = useState(2);
  const [hosts, setHosts] = useState([defaultHost(), defaultHost()]);

  // 에피소드 설정
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('');
  const [coreMessage, setCoreMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState('정보전달형');
  const [duration, setDuration] = useState(20);
  const [avoidTopics, setAvoidTopics] = useState('');

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);

  // 페르소나 상태
  const [personaLoading, setPersonaLoading] = useState([false, false, false]);
  const [personas, setPersonas] = useState([null, null, null]);
  const [selectedPersona, setSelectedPersona] = useState([null, null, null]);

  // 저장된 출연진 불러오기 모달
  const [castModal, setCastModal] = useState(null); // null | { hostIdx, members }

  const updateHost = (i, field, value) => {
    setHosts(prev => prev.map((h, idx) => idx === i ? { ...h, [field]: value } : h));
  };

  const addHost = () => {
    if (hostCount >= 3) return;
    setHostCount(c => c + 1);
    setHosts(prev => [...prev, defaultHost()]);
  };

  const removeHost = () => {
    if (hostCount <= 2) return;
    setHostCount(c => c - 1);
    setHosts(prev => prev.slice(0, -1));
  };

  // 페르소나 제안 (AI)
  const suggestPersona = async (i) => {
    const host = hosts[i];
    if (!host.name || !host.job) {
      alert('이름과 직업을 먼저 입력해주세요');
      return;
    }
    setPersonaLoading(prev => prev.map((v, idx) => idx === i ? true : v));
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/script/podcast/persona', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: host.name, job: host.job,
          roleType: host.roleType, interests: host.interests,
          personality: host.personality,
        }),
      });
      const data = await res.json();
      setPersonas(prev => prev.map((v, idx) => idx === i ? data.personas : v));
    } catch (e) {
      alert('페르소나 제안 실패: ' + e.message);
    }
    setPersonaLoading(prev => prev.map((v, idx) => idx === i ? false : v));
  };

  // 페르소나 적용
  const applyPersona = (hostIdx, persona) => {
    setSelectedPersona(prev => prev.map((v, i) => i === hostIdx ? persona : v));
    updateHost(hostIdx, 'personaTag', persona.tag);
    updateHost(hostIdx, 'catchphrase', persona.catchphrase);
    updateHost(hostIdx, 'personaDesc', persona.description);
    updateHost(hostIdx, 'selfIntro', persona.intro);
  };

  // 저장된 출연진 목록 불러오기
  const loadCastMember = async (hostIdx) => {
    try {
      const r = await api.get('/cast');
      setCastModal({ hostIdx, members: r.data.members || [] });
    } catch (e) {
      alert('출연진 목록 로드 실패: ' + e.message);
    }
  };

  // 저장된 출연진 적용
  const applyCastMember = (hostIdx, member) => {
    updateHost(hostIdx, 'name', member.name || '');
    updateHost(hostIdx, 'job', member.job || '');
    updateHost(hostIdx, 'roleType', member.roleType || '호기심형_진행자');
    updateHost(hostIdx, 'personality', member.personality || '');
    updateHost(hostIdx, 'interests', member.interests || '');
    updateHost(hostIdx, 'episodeRole', member.defaultEpisodeRole || '');
    // 기본 페르소나 자동 적용
    const defaultPersona = member.personas?.find(p => p.isDefault) || member.personas?.[0];
    if (defaultPersona) {
      updateHost(hostIdx, 'personaTag', defaultPersona.tag || '');
      updateHost(hostIdx, 'catchphrase', defaultPersona.catchphrase || '');
      updateHost(hostIdx, 'personaDesc', defaultPersona.description || '');
      updateHost(hostIdx, 'selfIntro', defaultPersona.intro || '');
      setSelectedPersona(prev => prev.map((v, i) => i === hostIdx ? defaultPersona : v));
    }
    setCastModal(null);
  };

  const generate = async () => {
    if (!topic.trim()) { alert('주제를 입력해주세요'); return; }
    if (hosts.some(h => !h.name.trim())) { alert('출연자 이름을 모두 입력해주세요'); return; }

    setGenerating(true);
    setProgress({ percent: 0, message: '준비 중...' });

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/script/podcast/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ topic, goal, coreMessage, targetAudience, tone, duration, hosts, avoidTopics }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let scriptData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') setProgress(data);
            else if (data.type === 'done') { scriptData = data; }
          } catch {}
        }
      }

      if (scriptData) {
        // DB 저장 시도
        try {
          const saveRes = await fetch('/api/script/podcast/save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              script: scriptData.script,
              topic: scriptData.topic,
              hosts: scriptData.hosts,
              duration: scriptData.duration,
              tone: scriptData.tone,
            }),
          });
          const saveData = await saveRes.json();
          if (saveData.id) scriptData.id = saveData.id;
        } catch (e) {
          console.warn('팟캐스트 DB 저장 실패:', e);
        }
        // sessionStorage에 저장 후 뷰어로 이동
        sessionStorage.setItem('podcastScript', JSON.stringify(scriptData));
        navigate('/podcast/script');
      }
    } catch (err) {
      alert('생성 실패: ' + err.message);
    }
    setGenerating(false);
    setProgress(null);
  };

  // ── 스타일 ──
  const S = {
    wrap: {
      minHeight: '100vh',
      background: '#080C0F',
      color: '#E8EDF2',
      fontFamily: "'Noto Sans KR', sans-serif",
      padding: '40px 32px',
    },
    header: { marginBottom: 40 },
    title: {
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: 42, letterSpacing: 2,
      color: '#FF6B35', marginBottom: 6,
      display: 'flex', alignItems: 'center', gap: 12,
    },
    subtitle: { fontSize: 14, color: '#6B7A8D' },

    // 스텝 인디케이터
    steps: { display: 'flex', gap: 0, marginBottom: 40 },
    stepItem: (active, done) => ({
      flex: 1, padding: '12px 20px', textAlign: 'center',
      background: done ? '#FF6B3515' : active ? '#FF6B3520' : '#111820',
      borderBottom: `2px solid ${done || active ? '#FF6B35' : '#1E2A35'}`,
      fontSize: 13, fontWeight: 600,
      color: done ? '#FF6B35' : active ? '#FF6B35' : '#4A5568',
      cursor: 'pointer', transition: 'all .2s',
    }),

    // 카드
    card: {
      background: '#0E1520',
      border: '1px solid #1E2A35',
      borderRadius: 16, padding: 28, marginBottom: 20,
    },
    cardTitle: {
      fontSize: 16, fontWeight: 700, color: '#E8EDF2',
      marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
    },

    // 출연자 카드
    hostCard: (i) => ({
      background: '#111820',
      border: `1px solid ${['#FF6B35', '#00B4D8', '#4CAF50'][i] || '#1E2A35'}30`,
      borderLeft: `3px solid ${['#FF6B35', '#00B4D8', '#4CAF50'][i] || '#FF6B35'}`,
      borderRadius: 12, padding: 24, marginBottom: 16,
    }),
    hostTitle: (i) => ({
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: 18, letterSpacing: 1,
      color: ['#FF6B35', '#00B4D8', '#4CAF50'][i] || '#FF6B35',
      marginBottom: 16,
    }),

    // 입력
    label: { fontSize: 12, fontWeight: 600, color: '#6B7A8D', marginBottom: 6, display: 'block', letterSpacing: 0.5 },
    input: {
      width: '100%', padding: '10px 14px',
      background: '#080C0F', border: '1px solid #1E2A35',
      borderRadius: 8, color: '#E8EDF2', fontSize: 14,
      outline: 'none', boxSizing: 'border-box',
      fontFamily: "'Noto Sans KR', sans-serif",
      transition: 'border-color .2s',
    },
    select: {
      width: '100%', padding: '10px 14px',
      background: '#080C0F', border: '1px solid #1E2A35',
      borderRadius: 8, color: '#E8EDF2', fontSize: 14,
      outline: 'none', cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    textarea: {
      width: '100%', padding: '10px 14px',
      background: '#080C0F', border: '1px solid #1E2A35',
      borderRadius: 8, color: '#E8EDF2', fontSize: 14,
      outline: 'none', boxSizing: 'border-box', resize: 'vertical',
      fontFamily: "'Noto Sans KR', sans-serif",
      minHeight: '80px',
    },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
    mb16: { marginBottom: 16 },

    // 역할 선택 그리드
    roleGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 6 },
    roleItem: (selected) => ({
      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
      border: `1px solid ${selected ? '#FF6B35' : '#1E2A35'}`,
      background: selected ? '#FF6B3515' : '#080C0F',
      transition: 'all .15s',
    }),
    roleLabel: { fontSize: 13, fontWeight: 600, color: '#E8EDF2', marginBottom: 2 },
    roleDesc: { fontSize: 11, color: '#4A5568' },

    // 성격 태그
    tagGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
    tag: (selected) => ({
      padding: '5px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 12,
      border: `1px solid ${selected ? '#FF6B35' : '#1E2A35'}`,
      background: selected ? '#FF6B3520' : 'transparent',
      color: selected ? '#FF6B35' : '#6B7A8D',
      transition: 'all .15s',
    }),

    // 버튼
    btnPrimary: {
      padding: '14px 36px', background: '#FF6B35', border: 'none',
      borderRadius: 10, color: '#fff', fontSize: 15, fontWeight: 700,
      cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif",
      transition: 'all .2s',
    },
    btnSecondary: {
      padding: '14px 24px', background: 'transparent',
      border: '1px solid #1E2A35', borderRadius: 10,
      color: '#6B7A8D', fontSize: 14, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnAdd: {
      padding: '8px 16px', background: 'transparent',
      border: '1px dashed #1E2A35', borderRadius: 8,
      color: '#4A5568', fontSize: 13, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif", width: '100%',
      marginTop: 8,
    },

    // 진행바
    progressWrap: {
      background: '#0E1520', border: '1px solid #1E2A35',
      borderRadius: 12, padding: 24, marginTop: 20,
    },
    progressBar: (pct) => ({
      height: 6, background: '#1E2A35', borderRadius: 3, overflow: 'hidden',
    }),
    progressFill: (pct) => ({
      height: '100%', background: 'linear-gradient(90deg, #FF6B35, #FF9B35)',
      borderRadius: 3, width: `${pct}%`, transition: 'width 0.5s',
    }),

    // 음파 애니메이션
    wave: {
      display: 'flex', gap: 3, alignItems: 'center', height: 20,
    },
  };

  const toneOptions = [
    { value: '정보전달형', label: '📚 정보전달형', desc: '교육적, 전문적' },
    { value: '토크형', label: '💬 토크형', desc: '가볍고 재미있게' },
    { value: '스토리텔링형', label: '📖 스토리텔링형', desc: '감성적, 내러티브' },
    { value: '토론형', label: '⚡ 토론형', desc: '다양한 관점 충돌' },
    { value: '교육형', label: '🎓 교육형', desc: '강의식, 체계적' },
    { value: '인터뷰형', label: '🎤 인터뷰형', desc: '심층 인터뷰' },
  ];

  return (
    <div style={S.wrap}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Noto+Sans+KR:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* 헤더 */}
      <div style={S.header}>
        <div style={S.title}>
          <span>🎙</span> PODCAST SCRIPT STUDIO
        </div>
        <div style={S.subtitle}>출연진 프로필 기반 AI 팟캐스트 대본 자동 생성 · 50토큰 소비</div>
      </div>

      {/* 스텝 인디케이터 */}
      <div style={S.steps}>
        <div style={S.stepItem(step === 1, step > 1)} onClick={() => setStep(1)}>
          {step > 1 ? '✓ ' : '01 '}출연진 설정
        </div>
        <div style={S.stepItem(step === 2, false)} onClick={() => step > 1 && setStep(2)}>
          02 에피소드 설정
        </div>
      </div>

      {/* STEP 1: 출연진 */}
      {step === 1 && (
        <div>
          <div style={{ ...S.card, borderColor: '#FF6B3530' }}>
            <div style={S.cardTitle}>
              <span>👥</span> 출연진 구성
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {hostCount > 2 && (
                  <button style={{ ...S.btnSecondary, padding: '6px 14px', fontSize: 12 }}
                    onClick={removeHost}>− 출연자 제거</button>
                )}
                {hostCount < 3 && (
                  <button style={{ ...S.btnSecondary, padding: '6px 14px', fontSize: 12, borderColor: '#FF6B3540', color: '#FF6B35' }}
                    onClick={addHost}>+ 출연자 추가</button>
                )}
              </div>
            </div>

            {hosts.slice(0, hostCount).map((host, i) => (
              <div key={i} style={S.hostCard(i)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ ...S.hostTitle(i), marginBottom: 0 }}>
                    {['🎤 진행자 / 호스트 A', '🎧 공동진행 / 게스트 B', '🎵 패널 C'][i]}
                  </div>
                  <button
                    onClick={() => loadCastMember(i)}
                    style={{
                      padding: '6px 14px', background: 'transparent',
                      border: `1px solid ${['#FF6B35', '#00B4D8', '#4CAF50'][i] || '#1E2A35'}40`,
                      borderRadius: 8,
                      color: ['#FF6B35', '#00B4D8', '#4CAF50'][i] || '#FF6B35',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: "'Noto Sans KR', sans-serif",
                    }}>
                    👥 저장된 출연진 불러오기
                  </button>
                </div>

                <div style={S.grid2}>
                  <div style={S.mb16}>
                    <label style={S.label}>이름 *</label>
                    <input style={S.input} value={host.name}
                      onChange={e => updateHost(i, 'name', e.target.value)}
                      placeholder="예: 박승찬" />
                  </div>
                  <div style={S.mb16}>
                    <label style={S.label}>직업 / 현재 하는 일</label>
                    <input style={S.input} value={host.job}
                      onChange={e => updateHost(i, 'job', e.target.value)}
                      placeholder="예: 소아청소년과 한의사, 하이키한의원 원장" />
                  </div>
                </div>

                <div style={S.mb16}>
                  <label style={S.label}>역할 유형 선택</label>
                  <div style={S.roleGrid}>
                    {ROLE_TYPES.map(r => (
                      <div key={r.value} style={S.roleItem(host.roleType === r.value)}
                        onClick={() => updateHost(i, 'roleType', r.value)}>
                        <div style={S.roleLabel}>{r.label}</div>
                        <div style={S.roleDesc}>{r.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={S.mb16}>
                  <label style={S.label}>성격/말투</label>
                  <div style={S.tagGrid}>
                    {PERSONALITIES.map(p => (
                      <div key={p} style={S.tag(host.personality === p)}
                        onClick={() => updateHost(i, 'personality', host.personality === p ? '' : p)}>
                        {p}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={S.grid2}>
                  <div style={S.mb16}>
                    <label style={S.label}>관심분야 / 전문 영역</label>
                    <input style={S.input} value={host.interests}
                      onChange={e => updateHost(i, 'interests', e.target.value)}
                      placeholder="예: 아동 성장, AI 의료, 한방 치료" />
                  </div>
                  <div style={S.mb16}>
                    <label style={S.label}>이번 에피소드에서의 역할</label>
                    <input style={S.input} value={host.episodeRole}
                      onChange={e => updateHost(i, 'episodeRole', e.target.value)}
                      placeholder="예: 전문 지식 제공, 궁금한 점 질문" />
                  </div>
                </div>

                {/* 페르소나 제안 섹션 */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #1E2A35' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E8EDF2' }}>
                      ✨ 페르소나 설정
                    </div>
                    <button
                      onClick={() => suggestPersona(i)}
                      disabled={personaLoading[i]}
                      style={{
                        padding: '6px 14px', background: '#FF6B3520',
                        border: '1px solid #FF6B3540', borderRadius: 8,
                        color: '#FF6B35', fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif",
                      }}>
                      {personaLoading[i] ? '🔄 분석 중...' : '✨ AI 페르소나 제안'}
                    </button>
                  </div>

                  {/* 선택된 페르소나 표시 */}
                  {selectedPersona[i] && (
                    <div style={{
                      padding: '12px 16px', background: '#FF6B3510',
                      border: '1px solid #FF6B3530', borderRadius: 10, marginBottom: 12,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#FF6B35', marginBottom: 4 }}>
                        ✅ {selectedPersona[i].version}
                      </div>
                      <div style={{ fontSize: 12, color: '#E8EDF2', marginBottom: 4 }}>
                        "{selectedPersona[i].tag}"
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7A8D' }}>
                        말버릇: "{selectedPersona[i].catchphrase}"
                      </div>
                    </div>
                  )}

                  {/* 페르소나 3가지 옵션 */}
                  {personas[i] && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {personas[i].map((p, pi) => (
                        <div key={pi}
                          onClick={() => applyPersona(i, p)}
                          style={{
                            padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                            background: selectedPersona[i]?.version === p.version ? '#FF6B3515' : '#080C0F',
                            border: `1px solid ${selectedPersona[i]?.version === p.version ? '#FF6B35' : '#1E2A35'}`,
                            transition: 'all .15s',
                          }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8EDF2' }}>
                              {['🔥','💡','📖'][pi]} {p.version}
                            </div>
                            <div style={{ fontSize: 11, color: '#6B7A8D' }}>{p.strength}</div>
                          </div>
                          <div style={{ fontSize: 12, color: '#FF6B35', marginBottom: 4 }}>
                            "{p.tag}"
                          </div>
                          <div style={{ fontSize: 12, color: '#C8D0DA', marginBottom: 4, fontStyle: 'italic' }}>
                            자기소개: "{p.intro}"
                          </div>
                          <div style={{ fontSize: 11, color: '#6B7A8D' }}>
                            말버릇: "{p.catchphrase}"
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 직접 입력 필드 */}
                  <div style={{ marginTop: 12 }}>
                    <label style={S.label}>캐릭터 태그 (직접 입력 가능)</label>
                    <input style={S.input} value={hosts[i].personaTag || ''}
                      onChange={e => updateHost(i, 'personaTag', e.target.value)}
                      placeholder="예: 30년 임상의 살아있는 성장 백과사전" />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={S.label}>시그니처 말투</label>
                    <input style={S.input} value={hosts[i].catchphrase || ''}
                      onChange={e => updateHost(i, 'catchphrase', e.target.value)}
                      placeholder="예: 솔직히 말씀드리면..." />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <button style={S.btnPrimary} onClick={() => setStep(2)}>
              다음: 에피소드 설정 →
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: 에피소드 */}
      {step === 2 && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}><span>📻</span> 에피소드 기본 정보</div>

            <div style={S.mb16}>
              <label style={S.label}>주제 *</label>
              <input style={S.input} value={topic} onChange={e => setTopic(e.target.value)}
                placeholder="예: AI 시대에 한의사로 살아남는 법, 아이 키 성장의 숨겨진 비밀..." />
            </div>

            <div style={S.grid2}>
              <div style={S.mb16}>
                <label style={S.label}>에피소드 목표</label>
                <input style={S.input} value={goal} onChange={e => setGoal(e.target.value)}
                  placeholder="예: 청취자가 성장에 대한 새로운 시각을 얻게 한다" />
              </div>
              <div style={S.mb16}>
                <label style={S.label}>핵심 메시지</label>
                <input style={S.input} value={coreMessage} onChange={e => setCoreMessage(e.target.value)}
                  placeholder="예: 키 성장은 유전만이 아니다, 환경이 70%다" />
              </div>
            </div>

            <div style={S.mb16}>
              <label style={S.label}>타겟 청취자</label>
              <input style={S.input} value={targetAudience} onChange={e => setTargetAudience(e.target.value)}
                placeholder="예: 자녀 성장이 걱정인 부모, 20~40대 직장인..." />
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}><span>🎚️</span> 포맷 & 스타일</div>

            <div style={S.mb16}>
              <label style={S.label}>분위기/톤</label>
              <div style={S.grid3}>
                {toneOptions.map(t => (
                  <div key={t.value}
                    style={{ ...S.roleItem(tone === t.value), padding: '12px 14px' }}
                    onClick={() => setTone(t.value)}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E8EDF2', marginBottom: 2 }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: '#4A5568' }}>{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={S.grid2}>
              <div style={S.mb16}>
                <label style={S.label}>진행 시간</label>
                <select style={S.select} value={duration} onChange={e => setDuration(Number(e.target.value))}>
                  <option value={10}>10분 (짧은 에피소드)</option>
                  <option value={20}>20분 (일반 에피소드)</option>
                  <option value={30}>30분 (심층 에피소드)</option>
                </select>
              </div>
              <div style={S.mb16}>
                <label style={S.label}>피해야 할 내용 (선택)</label>
                <input style={S.input} value={avoidTopics} onChange={e => setAvoidTopics(e.target.value)}
                  placeholder="예: 특정 경쟁사 언급, 정치적 발언..." />
              </div>
            </div>
          </div>

          {/* 출연진 요약 */}
          <div style={{ ...S.card, background: '#0A1018' }}>
            <div style={S.cardTitle}><span>👥</span> 출연진 요약</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {hosts.slice(0, hostCount).map((h, i) => (
                <div key={i} style={{ flex: 1, padding: '12px 16px', background: '#0E1520', borderRadius: 10, borderLeft: `3px solid ${['#FF6B35','#00B4D8','#4CAF50'][i]}` }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#E8EDF2', marginBottom: 4 }}>
                    {h.name || '이름 미입력'}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7A8D' }}>{h.job || '-'}</div>
                  <div style={{ fontSize: 11, color: ['#FF6B35','#00B4D8','#4CAF50'][i], marginTop: 4 }}>
                    {ROLE_TYPES.find(r => r.value === h.roleType)?.label || h.roleType}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 진행 표시 */}
          {generating && progress && (
            <div style={S.progressWrap}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={S.wave}>
                  {[1,2,3,4,5].map(n => (
                    <div key={n} style={{
                      width: 3, background: '#FF6B35', borderRadius: 2,
                      height: `${8 + n * 3}px`,
                      animation: `wave${n} 0.8s ease-in-out infinite alternate`,
                      animationDelay: `${n * 0.1}s`,
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: 14, color: '#E8EDF2', fontWeight: 600 }}>{progress.message}</span>
                <span style={{ marginLeft: 'auto', fontSize: 14, color: '#FF6B35', fontWeight: 700 }}>{progress.percent}%</span>
              </div>
              <div style={S.progressBar()}>
                <div style={S.progressFill(progress.percent)} />
              </div>
            </div>
          )}

          <style>{`
            @keyframes wave1 { to { height: 18px; } }
            @keyframes wave2 { to { height: 22px; } }
            @keyframes wave3 { to { height: 26px; } }
            @keyframes wave4 { to { height: 22px; } }
            @keyframes wave5 { to { height: 18px; } }
          `}</style>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            <button style={S.btnSecondary} onClick={() => setStep(1)}>← 출연진 수정</button>
            <button
              style={{ ...S.btnPrimary, opacity: generating ? 0.7 : 1, minWidth: 200 }}
              onClick={generate} disabled={generating}>
              {generating ? '🎙️ 대본 생성 중...' : '🎙️ 대본 생성하기'}
            </button>
          </div>
        </div>
      )}

      {/* 저장된 출연진 선택 모달 */}
      {castModal && (
        <div
          onClick={() => setCastModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background: '#0E1520', border: '1px solid #1E2A35', borderRadius: 16,
              padding: 28, width: 480, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto',
            }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: '#FF6B35', letterSpacing: 1, marginBottom: 16 }}>
              👥 저장된 출연진 선택
            </div>
            {castModal.members.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#6B7A8D', fontSize: 13 }}>
                저장된 출연진이 없습니다.<br/>
                <span style={{ fontSize: 12 }}>사이드바 → 출연진 관리에서 등록하세요.</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {castModal.members.map((m, idx) => {
                  const defaultPersona = m.personas?.find(p => p.isDefault) || m.personas?.[0];
                  return (
                    <div key={m._id}
                      onClick={() => applyCastMember(castModal.hostIdx, m)}
                      style={{
                        padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                        background: '#080C0F', border: '1px solid #1E2A35',
                        borderLeft: '3px solid #FF6B35', transition: 'all .15s',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#E8EDF2' }}>{m.name}</div>
                        <div style={{ fontSize: 11, color: '#6B7A8D' }}>
                          ✨ {m.personas?.length || 0}개 페르소나
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7A8D', marginBottom: 4 }}>{m.job || '-'}</div>
                      {defaultPersona?.tag && (
                        <div style={{ fontSize: 11, color: '#FF6B35', fontStyle: 'italic' }}>
                          ⭐ "{defaultPersona.tag}"
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setCastModal(null)}
                style={{
                  padding: '10px 20px', background: 'transparent',
                  border: '1px solid #1E2A35', borderRadius: 8, color: '#6B7A8D',
                  fontSize: 13, cursor: 'pointer', fontFamily: "'Noto Sans KR', sans-serif",
                }}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
